/**
 * Converter format and quality catalogues.
 *
 * Kept out of the component file so the option lists and the default-picking
 * rules can be imported without dragging a React component along (and so Fast
 * Refresh keeps working for ConversionSettings.tsx).
 *
 * The quality presets mirror AUDIO_QUALITY_PRESETS / VIDEO_QUALITY_PRESETS in
 * constants/media_conversion.py — keep them in sync.
 */

export type ConversionQuality = 'low' | 'medium' | 'high' | 'very_high' | 'lossless';

export interface FormatOption {
  value: string;
  label: string;
  description: string;
}

export interface QualityOption {
  value: ConversionQuality;
  labelKey: string;
  labelDefault: string;
  hintKey: string;
  hintDefault: string;
  recommended?: boolean;
}

// ============================================================================
// Format Options
// ============================================================================

export const AUDIO_FORMATS: FormatOption[] = [
  { value: '.mp3', label: 'MP3', description: 'Most compatible' },
  { value: '.aac', label: 'AAC', description: 'High quality, small size' },
  { value: '.m4a', label: 'M4A', description: 'Apple format' },
  { value: '.ogg', label: 'OGG', description: 'Open source' },
  { value: '.opus', label: 'OPUS', description: 'Best for voice' },
  { value: '.flac', label: 'FLAC', description: 'Lossless' },
  { value: '.wav', label: 'WAV', description: 'Uncompressed' },
  { value: '.wma', label: 'WMA', description: 'Windows Media' },
  { value: '.ac3', label: 'AC3', description: 'Dolby Digital' },
  { value: '.eac3', label: 'EAC3', description: 'Dolby Digital Plus' },
  { value: '.mp2', label: 'MP2', description: 'MPEG Audio Layer 2' },
  { value: '.amr', label: 'AMR', description: 'Mobile voice' },
  { value: '.aiff', label: 'AIFF', description: 'Apple lossless' },
  { value: '.caf', label: 'CAF', description: 'Core Audio Format' },
  { value: '.au', label: 'AU', description: 'Sun/Unix audio' },
  { value: '.alac', label: 'ALAC', description: 'Apple Lossless' },
  { value: '.mka', label: 'MKA', description: 'Matroska Audio' },
];

export const VIDEO_FORMATS: FormatOption[] = [
  { value: '.mp4', label: 'MP4', description: 'Universal' },
  { value: '.webm', label: 'WebM', description: 'Web optimized' },
  { value: '.mkv', label: 'MKV', description: 'Container format' },
  { value: '.mov', label: 'MOV', description: 'Apple format' },
  { value: '.avi', label: 'AVI', description: 'Legacy format' },
  { value: '.wmv', label: 'WMV', description: 'Windows Media' },
  { value: '.flv', label: 'FLV', description: 'Flash Video' },
  { value: '.m4v', label: 'M4V', description: 'iTunes Video' },
  { value: '.ts', label: 'TS', description: 'Transport Stream' },
  { value: '.mpg', label: 'MPG', description: 'MPEG Video' },
  { value: '.mpeg', label: 'MPEG', description: 'MPEG Video' },
  { value: '.ogv', label: 'OGV', description: 'Ogg Video' },
  { value: '.gif', label: 'GIF', description: 'Animated GIF' },
  { value: '.3gp', label: '3GP', description: 'Mobile video' },
  { value: '.vob', label: 'VOB', description: 'DVD Video' },
  { value: '.mxf', label: 'MXF', description: 'Broadcast format' },
];

// ============================================================================
// Quality Options
// ============================================================================

/** Audio presets: bitrate + sample rate, with FLAC for "lossless". */
export const AUDIO_QUALITY_OPTIONS: QualityOption[] = [
  {
    value: 'low',
    labelKey: 'converter.quality.low',
    labelDefault: 'Low',
    hintKey: 'converter.quality.audio.low',
    hintDefault: '96 kbps',
  },
  {
    value: 'medium',
    labelKey: 'converter.quality.medium',
    labelDefault: 'Medium',
    hintKey: 'converter.quality.audio.medium',
    hintDefault: '128 kbps',
    recommended: true,
  },
  {
    value: 'high',
    labelKey: 'converter.quality.high',
    labelDefault: 'High',
    hintKey: 'converter.quality.audio.high',
    hintDefault: '192 kbps',
  },
  {
    value: 'very_high',
    labelKey: 'converter.quality.veryHigh',
    labelDefault: 'Very High',
    hintKey: 'converter.quality.audio.veryHigh',
    hintDefault: '320 kbps',
  },
  {
    value: 'lossless',
    labelKey: 'converter.quality.lossless',
    labelDefault: 'Lossless',
    hintKey: 'converter.quality.audio.lossless',
    hintDefault: 'FLAC only',
  },
];

/**
 * Video presets: CRF + encoder preset. There is deliberately NO "lossless"
 * entry — it is an audio-only (FLAC) concept and the backend rejects it for
 * video instead of silently encoding at "medium".
 */
export const VIDEO_QUALITY_OPTIONS: QualityOption[] = [
  {
    value: 'low',
    labelKey: 'converter.quality.low',
    labelDefault: 'Low',
    hintKey: 'converter.quality.video.low',
    hintDefault: 'smallest file',
  },
  {
    value: 'medium',
    labelKey: 'converter.quality.medium',
    labelDefault: 'Medium',
    hintKey: 'converter.quality.video.medium',
    hintDefault: 'balanced',
    recommended: true,
  },
  {
    value: 'high',
    labelKey: 'converter.quality.high',
    labelDefault: 'High',
    hintKey: 'converter.quality.video.high',
    hintDefault: 'high quality',
  },
  {
    value: 'very_high',
    labelKey: 'converter.quality.veryHigh',
    labelDefault: 'Very High',
    hintKey: 'converter.quality.video.veryHigh',
    hintDefault: 'near-original',
  },
];

// ============================================================================
// Selection Rules
// ============================================================================

export function getFormatOptions(mediaType: 'audio' | 'video'): FormatOption[] {
  return mediaType === 'audio' ? AUDIO_FORMATS : VIDEO_FORMATS;
}

export function getQualityOptions(mediaType: 'audio' | 'video'): QualityOption[] {
  return mediaType === 'audio' ? AUDIO_QUALITY_OPTIONS : VIDEO_QUALITY_OPTIONS;
}

/**
 * Keep the selected quality valid for the media type.
 *
 * Switching from an audio file to a video file must not leave "lossless"
 * selected: video has no lossless preset and the backend rejects it.
 */
export function clampQualityForMediaType(
  quality: ConversionQuality,
  mediaType: 'audio' | 'video'
): ConversionQuality {
  const options = getQualityOptions(mediaType);
  if (options.some((option) => option.value === quality)) {
    return quality;
  }
  return 'very_high';
}

/**
 * Output format to pre-select for a given input.
 *
 * Never pre-selects the input's own format: the default action must be a real
 * conversion, not a silent recompression the user did not ask for. Same-format
 * recompression stays available, but only as a deliberate choice.
 */
export function getDefaultOutputFormat(
  mediaType: 'audio' | 'video',
  inputExtension: string
): string {
  const preferred = mediaType === 'video' ? '.mp4' : '.mp3';
  if (inputExtension !== preferred) {
    return preferred;
  }
  const alternative = getFormatOptions(mediaType).find((format) => format.value !== inputExtension);
  return alternative ? alternative.value : preferred;
}
